import { useState, useMemo, useCallback } from 'react';
import { trpc } from '@/lib/trpc';
import { useAuth } from '@/_core/hooks/useAuth';
import { Card, CardContent, CardHeader, CardTitle } from '@/components/ui/card';
import { Badge } from '@/components/ui/badge';
import { Button } from '@/components/ui/button';
import { Textarea } from '@/components/ui/textarea';
import { Separator } from '@/components/ui/separator';
import {
  Users, User, Activity, Dumbbell, Scale, Award,
  ChevronLeft, StickyNote, Plus, Trash2, Pencil, Check, X,
  TrendingUp, Target, Loader2, Calendar, Clock, CalendarPlus, ChevronRight,
  AlertCircle, Flame,
} from 'lucide-react';
import { toast } from 'sonner';
import { useSubscription } from '@/hooks/useSubscription';
import { Link } from 'wouter';
import { Crown } from 'lucide-react';
import { format, parseISO, addDays, startOfWeek, isSameDay } from 'date-fns';
import type { PlannedExercise, WorkoutSet, Exercise } from '@/lib/types';
import { Input } from '@/components/ui/input';
import { Dialog, DialogContent, DialogHeader, DialogTitle } from '@/components/ui/dialog';
import ExerciseLibrary from '@/components/ExerciseLibrary';
import {
  ResponsiveContainer, BarChart, Bar, XAxis, YAxis, Tooltip,
  LineChart, Line, CartesianGrid,
} from 'recharts';

// ─── Types ────────────────────────────────────────────────────────────────────

type ClientRow = {
  user: {
    id: number;
    name: string | null;
    email: string | null;
    role: string;
    lastSignedIn: Date;
  };
  profile: {
    fitnessGoal: string | null;
    heightFeet: number | null;
    heightInches: number | null;
  } | null;
  // At-a-glance stats enriched by getTrainerClients
  totalSessions?: number;
  lastWorkoutDate?: string | null;
  lastWorkoutName?: string | null;
  streak?: number;
};

const GOAL_LABELS: Record<string, string> = {
  build_muscle: 'Build Muscle',
  lose_fat: 'Lose Fat',
  increase_strength: 'Increase Strength',
  improve_endurance: 'Improve Endurance',
  general_fitness: 'General Fitness',
};

// ─── Roster Card ─────────────────────────────────────────────────────────────

function ClientCard({ client, onClick }: { client: ClientRow; onClick: () => void }) {
  const initials = (client.user.name ?? 'U')
    .split(' ')
    .map((n) => n[0])
    .join('')
    .toUpperCase()
    .slice(0, 2);

  const goal = client.profile?.fitnessGoal ? GOAL_LABELS[client.profile.fitnessGoal] ?? client.profile.fitnessGoal : null;
  const lastWorkoutLabel = client.lastWorkoutDate
    ? `${format(new Date(client.lastWorkoutDate + 'T00:00:00'), 'MMM d')}${client.lastWorkoutName ? ` — ${client.lastWorkoutName}` : ''}`
    : 'No workouts yet';

  return (
    <Card
      className="cursor-pointer hover:border-primary/50 transition-all hover:shadow-md active:scale-[0.99]"
      onClick={onClick}
    >
      <CardContent className="p-4 flex items-center gap-4">
        <div className="h-12 w-12 rounded-full bg-primary/10 border border-primary/20 flex items-center justify-center shrink-0">
          <span className="font-mono font-bold text-primary text-sm">{initials}</span>
        </div>
        <div className="flex-1 min-w-0">
          <p className="font-semibold truncate">{client.user.name ?? 'Unknown'}</p>
          <p className="text-xs text-muted-foreground truncate">{client.user.email ?? '—'}</p>
          <div className="flex items-center gap-2 mt-1.5 flex-wrap">
            {goal && (
              <Badge variant="secondary" className="text-[10px] font-mono px-1.5 py-0.5">
                <Target className="h-2.5 w-2.5 mr-1" />
                {goal}
              </Badge>
            )}
            {(client.streak ?? 0) > 0 && (
              <Badge variant="outline" className="text-[10px] font-mono px-1.5 py-0.5 border-orange-500/40 text-orange-500">
                <Flame className="h-2.5 w-2.5 mr-1" />
                {client.streak}d streak
              </Badge>
            )}
            {typeof client.totalSessions === 'number' && (
              <Badge variant="outline" className="text-[10px] font-mono px-1.5 py-0.5">
                <Dumbbell className="h-2.5 w-2.5 mr-1" />
                {client.totalSessions} sessions
              </Badge>
            )}
          </div>
          <p className="text-[10px] text-muted-foreground font-mono mt-1 truncate">
            Last: {lastWorkoutLabel}
          </p>
        </div>
        <ChevronLeft className="h-4 w-4 text-muted-foreground rotate-180 shrink-0" />
      </CardContent>
    </Card>
  );
}

// ─── Stat Tile ────────────────────────────────────────────────────────────────

function StatTile({ label, value, icon: Icon, accent }: { label: string; value: string | number; icon: React.ElementType; accent?: string }) {
  return (
    <div className="bg-card border border-border rounded-lg p-4 flex flex-col gap-2">
      <div className="flex items-center gap-2 text-muted-foreground">
        <Icon className={`h-4 w-4 ${accent ?? ''}`} />
        <span className="text-xs font-mono uppercase tracking-wider">{label}</span>
      </div>
      <p className="text-2xl font-bold font-mono">{value}</p>
    </div>
  );
}

// ─── Coaching Notes Panel ─────────────────────────────────────────────────────

function CoachingNotes({ clientId }: { clientId: number }) {
  const utils = trpc.useUtils();
  const { data: notes, isLoading } = trpc.trainer.notes.useQuery({ clientId });
  const [newNote, setNewNote] = useState('');
  const [editingId, setEditingId] = useState<number | null>(null);
  const [editContent, setEditContent] = useState('');

  const addMutation = trpc.trainer.addNote.useMutation({
    onSuccess: () => {
      utils.trainer.notes.invalidate({ clientId });
      setNewNote('');
      toast.success('Note added');
    },
    onError: () => toast.error('Failed to add note'),
  });

  const updateMutation = trpc.trainer.updateNote.useMutation({
    onSuccess: () => {
      utils.trainer.notes.invalidate({ clientId });
      setEditingId(null);
      toast.success('Note updated');
    },
    onError: () => toast.error('Failed to update note'),
  });

  const deleteMutation = trpc.trainer.deleteNote.useMutation({
    onSuccess: () => {
      utils.trainer.notes.invalidate({ clientId });
      toast.success('Note deleted');
    },
    onError: () => toast.error('Failed to delete note'),
  });

  return (
    <Card>
      <CardHeader className="pb-3">
        <CardTitle className="text-sm font-mono uppercase tracking-wider flex items-center gap-2">
          <StickyNote className="h-4 w-4 text-yellow-500" />
          Coaching Notes
          <Badge variant="outline" className="ml-auto text-xs">{notes?.length ?? 0}</Badge>
        </CardTitle>
      </CardHeader>
      <CardContent className="space-y-3">
        {/* New note input */}
        <div className="flex flex-col gap-2">
          <Textarea
            placeholder="Add a private coaching note…"
            value={newNote}
            onChange={(e) => setNewNote(e.target.value)}
            className="text-sm resize-none min-h-[72px]"
            maxLength={2000}
          />
          <Button
            size="sm"
            onClick={() => { if (newNote.trim()) addMutation.mutate({ clientId, content: newNote.trim() }); }}
            disabled={!newNote.trim() || addMutation.isPending}
            className="self-end"
          >
            {addMutation.isPending ? <Loader2 className="h-3.5 w-3.5 animate-spin" /> : <Plus className="h-3.5 w-3.5 mr-1" />}
            Add Note
          </Button>
        </div>

        <Separator />

        {/* Notes list */}
        {isLoading ? (
          <div className="flex justify-center py-4">
            <Loader2 className="h-5 w-5 animate-spin text-muted-foreground" />
          </div>
        ) : notes?.length === 0 ? (
          <p className="text-xs text-muted-foreground text-center py-3 font-mono">NO NOTES YET</p>
        ) : (
          <div className="space-y-2 max-h-72 overflow-y-auto pr-1">
            {notes?.map((note) => (
              <div key={note.id} className="bg-secondary/40 rounded-md p-3 text-sm group">
                {editingId === note.id ? (
                  <div className="space-y-2">
                    <Textarea
                      value={editContent}
                      onChange={(e) => setEditContent(e.target.value)}
                      className="text-sm resize-none min-h-[60px]"
                      maxLength={2000}
                      autoFocus
                    />
                    <div className="flex gap-1.5 justify-end">
                      <Button size="sm" variant="ghost" onClick={() => setEditingId(null)}>
                        <X className="h-3 w-3" />
                      </Button>
                      <Button
                        size="sm"
                        onClick={() => updateMutation.mutate({ noteId: note.id, content: editContent.trim() })}
                        disabled={!editContent.trim() || updateMutation.isPending}
                      >
                        <Check className="h-3 w-3" />
                      </Button>
                    </div>
                  </div>
                ) : (
                  <div className="flex gap-2">
                    <div className="flex-1 min-w-0">
                      <p className="whitespace-pre-wrap break-words">{note.content}</p>
                      <p className="text-[10px] text-muted-foreground font-mono mt-1.5">
                        {format(new Date(note.createdAt), 'MMM d, yyyy h:mm a')}
                        {note.updatedAt > note.createdAt ? ' · edited' : ''}
                      </p>
                    </div>
                    <div className="flex flex-col gap-1 opacity-0 group-hover:opacity-100 transition-opacity shrink-0">
                      <button
                        onClick={() => { setEditingId(note.id); setEditContent(note.content); }}
                        className="text-muted-foreground hover:text-foreground transition-colors"
                      >
                        <Pencil className="h-3.5 w-3.5" />
                      </button>
                      <button
                        onClick={() => deleteMutation.mutate({ noteId: note.id })}
                        className="text-muted-foreground hover:text-destructive transition-colors"
                      >
                        <Trash2 className="h-3.5 w-3.5" />
                      </button>
                    </div>
                  </div>
                )}
              </div>
            ))}
          </div>
        )}
      </CardContent>
    </Card>
  );
}

// ─── Trainer Workout Builder Dialog ─────────────────────────────────────────

const DAYS_SHORT = ['Sun', 'Mon', 'Tue', 'Wed', 'Thu', 'Fri', 'Sat'];

type AssignedWorkoutDraft = {
  name: string;
  notes: string;
  scheduledDate: string;
  scheduledTime: string;
  targetDurationMinutes: string; // stored as string for input, parsed on save
  exercises: PlannedExercise[];
};

function makeDefaultSet(): WorkoutSet {
  return { id: `set-${Date.now()}-${Math.random().toString(36).slice(2, 5)}`, reps: undefined, weight: undefined, completed: false };
}

function AssignWorkoutDialog({
  clientId,
  clientName,
  scheduledDate,
  scheduledTime,
  existingWorkout,
  onClose,
}: {
  clientId: number;
  clientName: string;
  scheduledDate: string;
  scheduledTime: string;
  existingWorkout?: { id: string; name: string; notes?: string | null; targetDurationMinutes?: number | null; exercises: PlannedExercise[] } | null;
  onClose: () => void;
}) {
  const utils = trpc.useUtils();
  const { data: exercises = [] } = trpc.exercises.list.useQuery();
  const [showLibrary, setShowLibrary] = useState(false);
  const [draft, setDraft] = useState<AssignedWorkoutDraft>(() => ({
    name: existingWorkout?.name ?? 'Assigned Workout',
    notes: existingWorkout?.notes ?? '',
    scheduledDate,
    scheduledTime,
    targetDurationMinutes: existingWorkout?.targetDurationMinutes != null ? String(existingWorkout.targetDurationMinutes) : '',
    exercises: existingWorkout?.exercises ?? [],
  }));

  const exerciseMap = useMemo(() => new Map(exercises.map((e) => [e.id, e])), [exercises]);

  const assignMutation = trpc.trainer.assignWorkout.useMutation({
    onSuccess: () => {
      utils.trainer.assignedWorkouts.invalidate({ clientId });
      toast.success(`Workout assigned to ${clientName}`);
      onClose();
    },
    onError: () => toast.error('Failed to assign workout'),
  });

  const updateMutation = trpc.trainer.updateAssignedWorkout.useMutation({
    onSuccess: () => {
      utils.trainer.assignedWorkouts.invalidate({ clientId });
      toast.success('Workout updated');
      onClose();
    },
    onError: () => toast.error('Failed to update workout'),
  });

  const addExercise = useCallback((ex: Exercise) => {
    if (draft.exercises.some((pe) => pe.exerciseId === ex.id)) {
      toast.warning(`"${ex.name}" is already in this workout`);
      return;
    }
    setDraft((prev) => ({
      ...prev,
      exercises: [...prev.exercises, { id: `pe-${Date.now()}`, exerciseId: ex.id, sets: [makeDefaultSet()] }],
    }));
    setShowLibrary(false);
    toast.success(`Added "${ex.name}"`);
  }, [draft.exercises]);

  const removeExercise = (peId: string) =>
    setDraft((prev) => ({ ...prev, exercises: prev.exercises.filter((pe) => pe.id !== peId) }));

  const addSet = (peId: string) =>
    setDraft((prev) => ({
      ...prev,
      exercises: prev.exercises.map((pe) =>
        pe.id !== peId ? pe : { ...pe, sets: [...pe.sets, makeDefaultSet()] }
      ),
    }));

  const removeSet = (peId: string, setId: string) =>
    setDraft((prev) => ({
      ...prev,
      exercises: prev.exercises.map((pe) =>
        pe.id !== peId ? pe : { ...pe, sets: pe.sets.filter((s) => s.id !== setId) }
      ),
    }));

  const updateSet = (peId: string, setId: string, field: 'reps' | 'weight', value: string) =>
    setDraft((prev) => ({
      ...prev,
      exercises: prev.exercises.map((pe) =>
        pe.id !== peId ? pe : {
          ...pe,
          sets: pe.sets.map((s) =>
            s.id !== setId ? s : { ...s, [field]: value === '' ? undefined : Number(value) }
          ),
        }
      ),
    }));

  const handleSave = () => {
    if (!draft.name.trim()) { toast.error('Workout needs a name'); return; }
    if (draft.exercises.length === 0) { toast.error('Add at least one exercise'); return; }
    const parsedDuration = draft.targetDurationMinutes.trim() !== '' ? parseInt(draft.targetDurationMinutes, 10) : undefined;
    const payload = {
      clientId,
      scheduledDate: draft.scheduledDate,
      scheduledTime: draft.scheduledTime || undefined,
      name: draft.name.trim(),
      notes: draft.notes.trim() || undefined,
      targetDurationMinutes: parsedDuration && !isNaN(parsedDuration) ? parsedDuration : undefined,
      exercises: draft.exercises,
    };
    if (existingWorkout) {
      updateMutation.mutate({ workoutId: existingWorkout.id, ...payload });
    } else {
      assignMutation.mutate(payload);
    }
  };

  const isPending = assignMutation.isPending || updateMutation.isPending;

  return (
    <Dialog open onOpenChange={(open) => !open && onClose()}>
      <DialogContent className="max-w-2xl max-h-[90vh] overflow-y-auto">
        <DialogHeader>
          <DialogTitle className="font-mono text-sm uppercase tracking-wider">
            {existingWorkout ? 'Edit' : 'Assign'} Workout — {clientName}
          </DialogTitle>
        </DialogHeader>

        <div className="space-y-4 pt-2">
          <div className="grid grid-cols-2 gap-3">
            <div className="col-span-2">
              <label className="font-mono text-[10px] uppercase tracking-wider text-muted-foreground">Workout Name</label>
              <Input value={draft.name} onChange={(e) => setDraft((p) => ({ ...p, name: e.target.value }))}
                className="mt-1 font-mono text-sm" placeholder="e.g. Push Day" />
            </div>
            <div>
              <label className="font-mono text-[10px] uppercase tracking-wider text-muted-foreground">Date</label>
              <Input type="date" value={draft.scheduledDate}
                onChange={(e) => setDraft((p) => ({ ...p, scheduledDate: e.target.value }))}
                className="mt-1 font-mono text-sm" />
            </div>
            <div>
              <label className="font-mono text-[10px] uppercase tracking-wider text-muted-foreground">Time (optional)</label>
              <Input type="time" value={draft.scheduledTime}
                onChange={(e) => setDraft((p) => ({ ...p, scheduledTime: e.target.value }))}
                className="mt-1 font-mono text-sm" />
            </div>
            <div>
              <label className="font-mono text-[10px] uppercase tracking-wider text-muted-foreground">Target Duration (min, optional)</label>
              <Input
                type="number"
                min={1}
                max={480}
                value={draft.targetDurationMinutes}
                onChange={(e) => setDraft((p) => ({ ...p, targetDurationMinutes: e.target.value }))}
                className="mt-1 font-mono text-sm"
                placeholder="e.g. 60"
              />
            </div>
            <div className="col-span-2">
              <label className="font-mono text-[10px] uppercase tracking-wider text-muted-foreground">Notes for client (optional)</label>
              <textarea value={draft.notes} onChange={(e) => setDraft((p) => ({ ...p, notes: e.target.value }))}
                className="mt-1 w-full font-mono text-sm bg-muted/30 border border-border/60 rounded-md px-3 py-2 resize-none min-h-[60px] text-foreground"
                placeholder="Focus on form, rest 90s between sets..." />
            </div>
          </div>

          <div className="space-y-3">
            <div className="flex items-center justify-between">
              <p className="font-mono text-[10px] uppercase tracking-wider text-muted-foreground">Exercises ({draft.exercises.length})</p>
              <Button size="sm" variant="outline" className="h-7 text-[10px] font-mono uppercase tracking-wider"
                onClick={() => setShowLibrary(true)}>
                <Plus className="h-3 w-3 mr-1" /> Add Exercise
              </Button>
            </div>

            {draft.exercises.length === 0 && (
              <div className="text-center py-6 border border-dashed border-border/60 rounded-md">
                <Dumbbell className="h-8 w-8 text-muted-foreground/40 mx-auto mb-2" />
                <p className="font-mono text-xs text-muted-foreground uppercase tracking-wider">No exercises yet</p>
              </div>
            )}

            {draft.exercises.map((pe) => {
              const ex = exerciseMap.get(pe.exerciseId);
              return (
                <div key={pe.id} className="border border-border/60 rounded-md p-3 space-y-2">
                  <div className="flex items-center justify-between">
                    <div>
                      <p className="font-mono text-sm font-bold">{ex?.name ?? pe.exerciseId}</p>
                      {ex?.category && <p className="font-mono text-[10px] text-muted-foreground uppercase tracking-wider">{ex.category}</p>}
                    </div>
                    <button onClick={() => removeExercise(pe.id)} className="text-muted-foreground hover:text-destructive transition-colors">
                      <Trash2 className="h-4 w-4" />
                    </button>
                  </div>
                  <div className="space-y-1.5">
                    <div className="grid grid-cols-[1.5rem_1fr_1fr_1.5rem] gap-2 items-center">
                      <span className="font-mono text-[9px] text-muted-foreground uppercase">#</span>
                      <span className="font-mono text-[9px] text-muted-foreground uppercase">Reps</span>
                      <span className="font-mono text-[9px] text-muted-foreground uppercase">Weight (lbs)</span>
                      <span />
                    </div>
                    {pe.sets.map((s, idx) => (
                      <div key={s.id} className="grid grid-cols-[1.5rem_1fr_1fr_1.5rem] gap-2 items-center">
                        <span className="font-mono text-[10px] text-muted-foreground text-center">{idx + 1}</span>
                        <Input type="number" placeholder="10" value={s.reps ?? ''}
                          onChange={(e) => updateSet(pe.id, s.id, 'reps', e.target.value)}
                          className="h-7 font-mono text-xs" min={1} />
                        <Input type="number" placeholder="135" value={s.weight ?? ''}
                          onChange={(e) => updateSet(pe.id, s.id, 'weight', e.target.value)}
                          className="h-7 font-mono text-xs" min={0} step={2.5} />
                        <button onClick={() => removeSet(pe.id, s.id)}
                          className="text-muted-foreground hover:text-destructive transition-colors" disabled={pe.sets.length <= 1}>
                          <X className="h-3.5 w-3.5" />
                        </button>
                      </div>
                    ))}
                    <Button variant="ghost" size="sm" className="h-6 text-[10px] font-mono uppercase tracking-wider w-full"
                      onClick={() => addSet(pe.id)}>
                      <Plus className="h-3 w-3 mr-1" /> Add Set
                    </Button>
                  </div>
                </div>
              );
            })}
          </div>

          <div className="flex justify-end gap-2 pt-2">
            <Button variant="ghost" onClick={onClose} disabled={isPending}>Cancel</Button>
            <Button onClick={handleSave} disabled={isPending}>
              {isPending ? <Loader2 className="h-4 w-4 animate-spin mr-1" /> : <CalendarPlus className="h-4 w-4 mr-1" />}
              {existingWorkout ? 'Update Workout' : 'Assign to Client'}
            </Button>
          </div>
        </div>

        {showLibrary && (
          <div className="fixed inset-0 z-50 bg-background/80 backdrop-blur-sm flex items-end justify-center sm:items-center">
            <div className="bg-card border border-border rounded-xl shadow-xl w-full max-w-lg max-h-[80vh] overflow-y-auto p-4">
              <div className="flex items-center justify-between mb-3">
                <p className="font-mono text-xs uppercase tracking-wider text-muted-foreground">Select Exercise</p>
                <button onClick={() => setShowLibrary(false)} className="text-muted-foreground hover:text-foreground">
                  <X className="h-4 w-4" />
                </button>
              </div>
              <ExerciseLibrary onSelectExercise={addExercise} showSelectButton />
            </div>
          </div>
        )}
      </DialogContent>
    </Dialog>
  );
}

// ─── Client Schedule View ─────────────────────────────────────────────────────

function ClientSchedule({ client }: { client: ClientRow }) {
  const utils = trpc.useUtils();
  const [weekOffset, setWeekOffset] = useState(0);
  const [builderSlot, setBuilderSlot] = useState<{ date: string; time: string } | null>(null);
  const [editingWorkout, setEditingWorkout] = useState<{ id: string; name: string; notes?: string | null; targetDurationMinutes?: number | null; exercises: PlannedExercise[]; scheduledDate: string; scheduledTime: string } | null>(null);

  const { data: availability = [] } = trpc.trainer.clientAvailability.useQuery({ clientId: client.user.id });
  const { data: assignedWorkouts = [] } = trpc.trainer.assignedWorkouts.useQuery({ clientId: client.user.id });

  const deleteMutation = trpc.trainer.deleteAssignedWorkout.useMutation({
    onSuccess: () => {
      utils.trainer.assignedWorkouts.invalidate({ clientId: client.user.id });
      toast.success('Workout removed');
    },
    onError: () => toast.error('Failed to remove workout'),
  });

  const weekStart = useMemo(() => {
    const base = startOfWeek(new Date(), { weekStartsOn: 0 });
    return addDays(base, weekOffset * 7);
  }, [weekOffset]);

  const weekDays = useMemo(() =>
    Array.from({ length: 7 }, (_, i) => addDays(weekStart, i)),
    [weekStart]
  );

  const availabilitySet = useMemo(() => new Set(availability.map((a) => a.dayOfWeek)), [availability]);

  const workoutsByDate = useMemo(() => {
    const map = new Map<string, typeof assignedWorkouts>();
    for (const w of assignedWorkouts) {
      const key = w.scheduledDate;
      if (!map.has(key)) map.set(key, []);
      map.get(key)!.push(w);
    }
    return map;
  }, [assignedWorkouts]);

  const getAvailabilityForDay = (day: Date) => availability.find((a) => a.dayOfWeek === day.getDay());

  return (
    <div className="space-y-4">
      <div className="flex items-center justify-between">
        <Button variant="ghost" size="sm" onClick={() => setWeekOffset((w) => w - 1)} className="h-8 px-2">
          <ChevronLeft className="h-4 w-4" />
        </Button>
        <p className="font-mono text-xs uppercase tracking-wider text-muted-foreground">
          {format(weekStart, 'MMM d')} – {format(addDays(weekStart, 6), 'MMM d, yyyy')}
        </p>
        <Button variant="ghost" size="sm" onClick={() => setWeekOffset((w) => w + 1)} className="h-8 px-2">
          <ChevronRight className="h-4 w-4" />
        </Button>
      </div>

      {availability.length === 0 && (
        <div className="flex items-center gap-2 p-3 bg-yellow-500/10 border border-yellow-500/30 rounded-md">
          <AlertCircle className="h-4 w-4 text-yellow-500 shrink-0" />
          <p className="font-mono text-[10px] uppercase tracking-wider text-yellow-500">
            {client.user.name?.split(' ')[0] ?? 'Client'} hasn't set their availability yet
          </p>
        </div>
      )}

      <div className="grid grid-cols-7 gap-1">
        {weekDays.map((day) => {
          const dateStr = format(day, 'yyyy-MM-dd');
          const isAvailable = availabilitySet.has(day.getDay());
          const avail = getAvailabilityForDay(day);
          const workouts = workoutsByDate.get(dateStr) ?? [];
          const isToday = isSameDay(day, new Date());

          return (
            <div
              key={dateStr}
              className={`min-h-[120px] rounded-md border p-1.5 flex flex-col gap-1 transition-colors ${
                isAvailable
                  ? 'border-primary/40 bg-primary/5 cursor-pointer hover:bg-primary/10'
                  : 'border-border/30 bg-muted/10 opacity-50'
              } ${isToday ? 'ring-1 ring-primary/60' : ''}`}
              onClick={() => isAvailable && setBuilderSlot({ date: dateStr, time: avail?.startTime ?? '06:00' })}
            >
              <div className="flex flex-col items-center">
                <span className={`font-mono text-[9px] uppercase tracking-wider ${
                  isToday ? 'text-primary font-bold' : 'text-muted-foreground'
                }`}>{DAYS_SHORT[day.getDay()]}</span>
                <span className={`font-mono text-xs font-bold ${
                  isToday ? 'text-primary' : 'text-foreground'
                }`}>{format(day, 'd')}</span>
                {avail && (
                  <span className="font-mono text-[8px] text-primary/70 mt-0.5">{avail.startTime}–{avail.endTime}</span>
                )}
              </div>

              {workouts.map((w) => (
                <div
                  key={w.id}
                  className="bg-primary/20 border border-primary/40 rounded-sm px-1 py-0.5 group relative"
                  onClick={(e) => e.stopPropagation()}
                >
                  <p className="font-mono text-[9px] font-bold text-primary truncate">{w.name}</p>
                  {w.scheduledTime && (
                    <p className="font-mono text-[8px] text-primary/70 flex items-center gap-0.5">
                      <Clock className="h-2 w-2" />{w.scheduledTime}
                    </p>
                  )}
                  <p className="font-mono text-[8px] text-muted-foreground">
                    {(w.exercises as PlannedExercise[]).length} exercises
                  </p>
                  <div className="absolute top-0.5 right-0.5 hidden group-hover:flex gap-0.5">
                    <button
                      onClick={() => setEditingWorkout({ ...w, exercises: w.exercises as PlannedExercise[], scheduledTime: w.scheduledTime ?? '', targetDurationMinutes: (w as { targetDurationMinutes?: number | null }).targetDurationMinutes ?? null })}
                      className="bg-card/80 rounded-sm p-0.5 text-muted-foreground hover:text-foreground"
                    >
                      <Pencil className="h-2.5 w-2.5" />
                    </button>
                    <button
                      onClick={() => deleteMutation.mutate({ workoutId: w.id })}
                      className="bg-card/80 rounded-sm p-0.5 text-muted-foreground hover:text-destructive"
                    >
                      <Trash2 className="h-2.5 w-2.5" />
                    </button>
                  </div>
                </div>
              ))}
            </div>
          );
        })}
      </div>

      {builderSlot && (
        <AssignWorkoutDialog
          clientId={client.user.id}
          clientName={client.user.name ?? 'Client'}
          scheduledDate={builderSlot.date}
          scheduledTime={builderSlot.time}
          onClose={() => setBuilderSlot(null)}
        />
      )}

      {editingWorkout && (
        <AssignWorkoutDialog
          clientId={client.user.id}
          clientName={client.user.name ?? 'Client'}
          scheduledDate={editingWorkout.scheduledDate}
          scheduledTime={editingWorkout.scheduledTime}
          existingWorkout={editingWorkout}
          onClose={() => setEditingWorkout(null)}
        />
      )}
    </div>
  );
}

// ─── Client Detail View ───────────────────────────────────────────────────────

type DetailTab = 'overview' | 'schedule';

function ClientDetail({ client, onBack }: { client: ClientRow; onBack: () => void }) {
  const [tab, setTab] = useState<DetailTab>('overview');
  const { data: stats, isLoading: statsLoading } = trpc.trainer.clientStats.useQuery({ clientId: client.user.id });
  const { data: sessions, isLoading: sessionsLoading } = trpc.trainer.clientSessions.useQuery({ clientId: client.user.id });

  const goal = client.profile?.fitnessGoal ? GOAL_LABELS[client.profile.fitnessGoal] ?? client.profile.fitnessGoal : null;

  return (
    <div className="space-y-6">
      {/* Back + header */}
      <div className="flex items-center gap-3">
        <Button variant="ghost" size="sm" onClick={onBack} className="gap-1.5 text-muted-foreground hover:text-foreground">
          <ChevronLeft className="h-4 w-4" />
          All Clients
        </Button>
      </div>

      <div className="flex items-center gap-4">
        <div className="h-14 w-14 rounded-full bg-primary/10 border border-primary/20 flex items-center justify-center shrink-0">
          <span className="font-mono font-bold text-primary text-lg">
            {(client.user.name ?? 'U').split(' ').map((n) => n[0]).join('').toUpperCase().slice(0, 2)}
          </span>
        </div>
        <div>
          <h2 className="text-xl font-bold">{client.user.name ?? 'Unknown'}</h2>
          <p className="text-sm text-muted-foreground">{client.user.email ?? '—'}</p>
          {goal && (
            <Badge variant="secondary" className="mt-1 text-xs font-mono">
              <Target className="h-3 w-3 mr-1" />
              {goal}
            </Badge>
          )}
        </div>
      </div>

      {/* Tab switcher */}
      <div className="flex gap-1 p-1 bg-muted/30 rounded-lg w-fit">
        {(['overview', 'schedule'] as DetailTab[]).map((t) => (
          <button
            key={t}
            onClick={() => setTab(t)}
            className={`px-4 py-1.5 rounded-md font-mono text-[11px] uppercase tracking-wider transition-all ${
              tab === t
                ? 'bg-primary text-primary-foreground shadow-sm'
                : 'text-muted-foreground hover:text-foreground'
            }`}
          >
            {t === 'overview' ? (
              <span className="flex items-center gap-1.5"><Activity className="h-3 w-3" /> Overview</span>
            ) : (
              <span className="flex items-center gap-1.5"><Calendar className="h-3 w-3" /> Schedule</span>
            )}
          </button>
        ))}
      </div>

      {tab === 'schedule' && <ClientSchedule client={client} />}

      {tab === 'overview' && <>

      {/* Stats grid */}
      {statsLoading ? (
        <div className="flex justify-center py-8">
          <Loader2 className="h-6 w-6 animate-spin text-muted-foreground" />
        </div>
      ) : stats ? (
        <>
          <div className="grid grid-cols-2 md:grid-cols-3 lg:grid-cols-6 gap-3">
            <StatTile label="Sessions" value={stats.totalSessions} icon={Dumbbell} accent="text-primary" />
            <StatTile label="Volume (lbs)" value={stats.totalVolumeLbs.toLocaleString()} icon={TrendingUp} accent="text-green-500" />
            <StatTile label="Total Sets" value={stats.totalSets} icon={Activity} accent="text-blue-400" />
            <StatTile label="PRs" value={stats.totalPRs} icon={Award} accent="text-yellow-400" />
            <StatTile label="Cardio" value={stats.totalCardioSessions} icon={Activity} accent="text-red-400" />
            <StatTile label="Distance (mi)" value={stats.totalDistanceMiles} icon={Scale} accent="text-purple-400" />
          </div>

          {/* Frequency chart */}
          {stats.last12Weeks.length > 0 && (
            <Card>
              <CardHeader className="pb-2">
                <CardTitle className="text-xs font-mono uppercase tracking-wider text-muted-foreground">
                  Workout Frequency — Last 12 Weeks
                </CardTitle>
              </CardHeader>
              <CardContent>
                <ResponsiveContainer width="100%" height={140}>
                  <BarChart data={stats.last12Weeks} margin={{ top: 4, right: 4, left: -24, bottom: 0 }}>
                    <XAxis
                      dataKey="week"
                      tickFormatter={(v) => format(parseISO(v), 'M/d')}
                      tick={{ fontSize: 9, fontFamily: 'monospace' }}
                    />
                    <YAxis allowDecimals={false} tick={{ fontSize: 9, fontFamily: 'monospace' }} />
                    <Tooltip
                      formatter={(v) => [`${v} sessions`, 'Workouts']}
                      labelFormatter={(l) => `Week of ${format(parseISO(l as string), 'MMM d')}`}
                      contentStyle={{ fontSize: 11, fontFamily: 'monospace' }}
                    />
                    <Bar dataKey="count" fill="hsl(var(--primary))" radius={[3, 3, 0, 0]} />
                  </BarChart>
                </ResponsiveContainer>
              </CardContent>
            </Card>
          )}

          {/* Weight trend */}
          {stats.weightTrend.length > 1 && (
            <Card>
              <CardHeader className="pb-2">
                <div className="flex items-center justify-between">
                  <CardTitle className="text-xs font-mono uppercase tracking-wider text-muted-foreground">
                    Weight Trend
                  </CardTitle>
                  <div className="flex gap-3 text-xs font-mono">
                    {stats.startWeight && <span className="text-muted-foreground">Start: <span className="text-foreground">{stats.startWeight} lbs</span></span>}
                    {stats.latestWeight && <span className="text-muted-foreground">Current: <span className="text-foreground">{stats.latestWeight} lbs</span></span>}
                    {stats.startWeight && stats.latestWeight && (
                      <span className={stats.latestWeight < stats.startWeight ? 'text-green-500' : stats.latestWeight > stats.startWeight ? 'text-red-400' : 'text-muted-foreground'}>
                        {stats.latestWeight > stats.startWeight ? '+' : ''}{(stats.latestWeight - stats.startWeight).toFixed(1)} lbs
                      </span>
                    )}
                  </div>
                </div>
              </CardHeader>
              <CardContent>
                <ResponsiveContainer width="100%" height={140}>
                  <LineChart data={stats.weightTrend} margin={{ top: 4, right: 4, left: -24, bottom: 0 }}>
                    <CartesianGrid strokeDasharray="3 3" stroke="hsl(var(--border))" />
                    <XAxis dataKey="date" tickFormatter={(v) => format(parseISO(v), 'M/d')} tick={{ fontSize: 9, fontFamily: 'monospace' }} />
                    <YAxis domain={['auto', 'auto']} tick={{ fontSize: 9, fontFamily: 'monospace' }} />
                    <Tooltip
                      formatter={(v) => [`${v} lbs`, 'Weight']}
                      labelFormatter={(l) => format(parseISO(l as string), 'MMM d, yyyy')}
                      contentStyle={{ fontSize: 11, fontFamily: 'monospace' }}
                    />
                    <Line type="monotone" dataKey="weight" stroke="hsl(var(--primary))" strokeWidth={2} dot={false} />
                  </LineChart>
                </ResponsiveContainer>
              </CardContent>
            </Card>
                    )}
          {/* Workout heatmap */}
          {stats.heatmapDays && stats.heatmapDays.length > 0 && (
            <Card>
              <CardHeader className="pb-2">
                <CardTitle className="text-xs font-mono uppercase tracking-wider text-muted-foreground">
                  Workout Activity — Last 12 Weeks
                </CardTitle>
              </CardHeader>
              <CardContent>
                <div className="overflow-x-auto">
                  <div className="grid gap-1" style={{ gridTemplateColumns: 'repeat(12, 1fr)' }}>
                    {Array.from({ length: 12 }).map((_, weekIdx) => (
                      <div key={weekIdx} className="flex flex-col gap-1">
                        {stats.heatmapDays.slice(weekIdx * 7, weekIdx * 7 + 7).map((day) => (
                          <div
                            key={day.date}
                            title={`${day.date}: ${day.count} session${day.count !== 1 ? 's' : ''}`}
                            className="w-full aspect-square rounded-sm"
                            style={{
                              backgroundColor:
                                day.count === 0
                                  ? 'hsl(var(--secondary))'
                                  : day.count === 1
                                  ? 'hsl(var(--primary) / 0.4)'
                                  : day.count === 2
                                  ? 'hsl(var(--primary) / 0.7)'
                                  : 'hsl(var(--primary))',
                            }}
                          />
                        ))}
                      </div>
                    ))}
                  </div>
                  <div className="flex items-center gap-2 mt-2 justify-end">
                    <span className="text-[9px] font-mono text-muted-foreground">Less</span>
                    {[0, 1, 2, 3].map((level) => (
                      <div
                        key={level}
                        className="h-2.5 w-2.5 rounded-sm"
                        style={{
                          backgroundColor:
                            level === 0
                              ? 'hsl(var(--secondary))'
                              : level === 1
                              ? 'hsl(var(--primary) / 0.4)'
                              : level === 2
                              ? 'hsl(var(--primary) / 0.7)'
                              : 'hsl(var(--primary))',
                        }}
                      />
                    ))}
                    <span className="text-[9px] font-mono text-muted-foreground">More</span>
                  </div>
                </div>
              </CardContent>
            </Card>
          )}
        </>
      ) : null}
      {/* Recent sessions */}
      <Card>
        <CardHeader className="pb-3">
          <CardTitle className="text-sm font-mono uppercase tracking-wider flex items-center gap-2">
            <Dumbbell className="h-4 w-4 text-primary" />
            Recent Workouts
          </CardTitle>
        </CardHeader>
        <CardContent>
          {sessionsLoading ? (
            <div className="flex justify-center py-4">
              <Loader2 className="h-5 w-5 animate-spin text-muted-foreground" />
            </div>
          ) : !sessions || sessions.length === 0 ? (
            <p className="text-xs text-muted-foreground text-center py-3 font-mono">NO COMPLETED SESSIONS YET</p>
          ) : (
            <div className="space-y-2">
              {sessions.slice(0, 10).map((s) => {
                const planned = s.plannedExercises as Array<{ sets: Array<{ completed: boolean; weight?: number; reps?: number }> }>;
                const volume = planned.reduce((acc, pe) =>
                  acc + pe.sets.reduce((b, set) => b + (set.completed && set.weight && set.reps ? set.weight * set.reps : 0), 0), 0);
                const sets = planned.reduce((acc, pe) => acc + pe.sets.filter((set) => set.completed).length, 0);
                return (
                  <div key={s.id} className="flex items-center justify-between py-2 border-b border-border/50 last:border-0">
                    <div>
                      <p className="text-sm font-medium">{s.name}</p>
                      <p className="text-xs text-muted-foreground font-mono">{format(parseISO(s.date), 'EEE, MMM d yyyy')}</p>
                    </div>
                    <div className="text-right text-xs font-mono text-muted-foreground">
                      <p>{sets} sets</p>
                      <p>{Math.round(volume).toLocaleString()} lbs</p>
                    </div>
                  </div>
                );
              })}
            </div>
          )}
        </CardContent>
      </Card>

      {/* Coaching notes */}
      <CoachingNotes clientId={client.user.id} />
      </>}
    </div>
  );
}

// ─── Main Trainer Dashboard ───────────────────────────────────────────────────

export default function TrainerDashboard() {
  const { user } = useAuth();
  const { isTrainer: hasTrainerPlan } = useSubscription();
  const [selectedClient, setSelectedClient] = useState<ClientRow | null>(null);

  const { data: clients, isLoading } = trpc.trainer.myClients.useQuery(undefined, {
    enabled: user?.role === 'trainer' || user?.role === 'admin',
  });

  // Subscription guard — trainer plan required
  if (!hasTrainerPlan && user?.role !== 'admin') {
    return (
      <div className="flex flex-col items-center justify-center py-16 gap-4 text-center">
        <div className="h-14 w-14 rounded-full bg-orange-500/10 flex items-center justify-center">
          <Crown className="h-7 w-7 text-orange-400" />
        </div>
        <div>
          <p className="font-mono text-sm font-bold text-foreground uppercase tracking-wider">Trainer Command Center</p>
          <p className="text-xs text-muted-foreground mt-1">Requires the Trainer plan ($9.99/mo)</p>
        </div>
        <Link href="/pricing">
          <button className="px-4 py-2 bg-orange-500 hover:bg-orange-400 text-black font-mono text-xs font-bold uppercase tracking-wider rounded-md transition-colors">
            Upgrade to Trainer
          </button>
        </Link>
      </div>
    );
  }

  // Role guard
  if (user && user.role !== 'trainer' && user.role !== 'admin') {
    return (
      <div className="flex flex-col items-center justify-center py-16 gap-3 text-center">
        <Users className="h-10 w-10 text-muted-foreground" />
        <p className="font-mono text-sm text-muted-foreground uppercase tracking-wider">Trainer Access Only</p>
      </div>
    );
  }

  if (selectedClient) {
    return <ClientDetail client={selectedClient} onBack={() => setSelectedClient(null)} />;
  }

  return (
    <div className="space-y-6">
      {/* Header */}
      <div className="flex items-center justify-between">
        <div>
          <h2 className="text-lg font-bold font-mono tracking-wider flex items-center gap-2">
            <Users className="h-5 w-5 text-primary" />
            CLIENT ROSTER
          </h2>
          <p className="text-xs text-muted-foreground font-mono mt-0.5">
            {isLoading ? '…' : `${clients?.length ?? 0} CLIENT${(clients?.length ?? 0) !== 1 ? 'S' : ''} LINKED`}
          </p>
        </div>
      </div>

      {/* Client list */}
      {isLoading ? (
        <div className="flex justify-center py-12">
          <Loader2 className="h-6 w-6 animate-spin text-muted-foreground" />
        </div>
      ) : !clients || clients.length === 0 ? (
        <div className="flex flex-col items-center gap-3 py-12 text-center">
          <User className="h-10 w-10 text-muted-foreground" />
          <p className="font-mono text-sm text-muted-foreground uppercase tracking-wider">No clients linked yet</p>
          <p className="text-xs text-muted-foreground max-w-xs">
            Ask your admin to link clients to your trainer account via the database.
          </p>
        </div>
      ) : (
        <div className="space-y-3">
          {clients.map((client) => (
            <ClientCard
              key={client.user.id}
              client={client as ClientRow}
              onClick={() => setSelectedClient(client as ClientRow)}
            />
          ))}
        </div>
      )}
    </div>
  );
}
